<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import Avatar from '@/components/Avatar.vue'
import Button from '@/components/Button.vue'
import { useAlert } from '@/composables/useToast'
import {
  getBlocklist,
  approveBlockRequest,
  rejectBlockRequest,
  unblockContact,
} from '@/api/blockRequests'

const store = useStore()
const { t } = useI18n()

const requests = ref([])
const blockedContacts = ref([])
const isLoaded = ref(false)
const busy = ref(false)

const currentUser = computed(() => store.getters['auth/getCurrentUser'])
const accountId = computed(() => currentUser.value?.accounts?.[0]?.id)

const dateFormatter = new Intl.DateTimeFormat('ru', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

const load = async () => {
  if (!accountId.value) return
  try {
    const { data } = await getBlocklist(accountId.value)
    requests.value = data.requests
    blockedContacts.value = data.blocked_contacts
    isLoaded.value = true
  } catch (error) {
    useAlert(t('BLOCKLIST.API.ERROR'))
  }
}

const act = async action => {
  if (busy.value) return
  busy.value = true
  try {
    await action()
    await load()
  } catch (error) {
    useAlert(t('BLOCKLIST.API.ERROR'))
  } finally {
    busy.value = false
  }
}

const approve = request =>
  act(() => approveBlockRequest(accountId.value, request.id))
const reject = request =>
  act(() => rejectBlockRequest(accountId.value, request.id))
// The dialog page opens whatever thread its `c` query names
const threadLink = conversationId => ({
  path: '/',
  query: { c: String(conversationId) },
})

const unblock = contact =>
  act(() => unblockContact(accountId.value, contact.id))

// The profile arrives after the page mounts on a cold load, so the account
// id is watched rather than read once
watch(accountId, load, { immediate: true })
</script>

<template>
  <div
    class="flex flex-col w-full h-full px-6 pt-4 pb-8 m-0 overflow-auto bg-n-surface-1"
  >
    <div class="flex items-start w-full mx-auto max-w-5xl">
      <div v-if="isLoaded" class="flex flex-col w-full h-full gap-6 font-inter">
        <div class="flex flex-col gap-2">
          <h1 class="text-heading-1 text-n-slate-12">
            {{ $t('BLOCKLIST.HEADER') }}
          </h1>
          <p class="text-body-main text-n-slate-11">
            {{ $t('BLOCKLIST.DESCRIPTION') }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ $t('BLOCKLIST.REQUESTS.TITLE') }}
          </h2>
          <p
            v-if="!requests.length"
            class="py-6 text-body-main text-n-slate-11"
          >
            {{ $t('BLOCKLIST.REQUESTS.EMPTY') }}
          </p>
          <div v-else class="border-t divide-y divide-n-weak border-n-weak">
            <div
              v-for="request in requests"
              :key="request.id"
              class="flex items-center justify-between gap-4 py-3"
            >
              <div class="flex items-center gap-3 min-w-0">
                <Avatar :name="request.contact.name || '?'" :size="36" />
                <div class="flex flex-col min-w-0">
                  <router-link
                    v-if="request.contact.conversation_id"
                    :to="threadLink(request.contact.conversation_id)"
                    class="truncate text-heading-3 text-n-slate-12 hover:underline"
                  >
                    {{ request.contact.name }}
                  </router-link>
                  <span v-else class="truncate text-heading-3 text-n-slate-12">
                    {{ request.contact.name }}
                  </span>
                  <span class="truncate text-xs text-n-slate-11">
                    {{
                      $t('BLOCKLIST.REQUESTS.META', {
                        name: request.requester_name,
                        date: dateFormatter.format(
                          new Date(request.created_at)
                        ),
                      })
                    }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <Button
                  sm
                  faded
                  slate
                  :label="$t('BLOCKLIST.REQUESTS.REJECT')"
                  :disabled="busy"
                  @click="reject(request)"
                />
                <Button
                  sm
                  ruby
                  :label="$t('BLOCKLIST.REQUESTS.APPROVE')"
                  :disabled="busy"
                  @click="approve(request)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ $t('BLOCKLIST.BLOCKED.TITLE') }}
          </h2>
          <p
            v-if="!blockedContacts.length"
            class="py-6 text-body-main text-n-slate-11"
          >
            {{ $t('BLOCKLIST.BLOCKED.EMPTY') }}
          </p>
          <div v-else class="border-t divide-y divide-n-weak border-n-weak">
            <div
              v-for="contact in blockedContacts"
              :key="contact.id"
              class="flex items-center justify-between gap-4 py-3"
            >
              <div class="flex items-center gap-3 min-w-0">
                <Avatar :name="contact.name || '?'" :size="36" />
                <router-link
                  v-if="contact.conversation_id"
                  :to="threadLink(contact.conversation_id)"
                  class="truncate text-heading-3 text-n-slate-12 hover:underline"
                >
                  {{ contact.name }}
                </router-link>
                <span v-else class="truncate text-heading-3 text-n-slate-12">
                  {{ contact.name }}
                </span>
              </div>
              <Button
                sm
                faded
                slate
                :label="$t('BLOCKLIST.BLOCKED.UNBLOCK')"
                :disabled="busy"
                @click="unblock(contact)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
